import React from 'react';
import {
  CapaQuotationChatMessage,
  CapaQuotationChatMessageType,
  CapaQuotationChatMessageRfq
} from '@generated/graphql';
import CapaHelperBubble from './CapaHelperBubble';
import QuotationBubble from './QuotationBubble';
import FileThumbnail from './FileThumbnail';
import RequestBubble from './RequestBubble';
import ResponseBubble from './ResponseBubble';
import QuotationConsultingStartBubble from './QuotationConsultingStartBubble';
import QuotationAdjustRequestBubble from './QuotationAdjustRequestBubble';
import AdjustedQuotationBubble from './AdjustedQuotationBubble';
import QuotationAgreementBubble from './QuotationAgreementBubble';
import PurchaseOrderConsultingStartBubble from './PurchaseOrderConsultingStartBubble';
import PurchaseOrderAdjustRequestBubble from './PurchaseOrderAdjustRequestBubble';
import AdjustedPurchaseOrderBubble from './AdjustedPurchaseOrderBubble';
import PurchaseOrderAgreementBubble from './PurchaseOrderAgreementBubble';
import RfqQuotationRegisterRequestBubble from './RfqQuotationRegisterRequestBubble';
import DefaultBubble from '../CommonBubble/DefaultBubble';
import InduceQuotationRequestBubble from './InduceQuotationRequestBubble';
import RejectRfqPurchaseOrderBubble from './RejectRfqPurchaseOrderBubble';
import IntroductionConsultingStartBubble from './IntroductionConsultingStartBubble';
import InducePurchaseOrderIssueBubble from './InducePurchaseOrderIssueBubble';
import NewAnnotationBubble from './NewAnnotationBubble';
import NewAnnotationCommentBubble from './NewAnnotationCommentBubble';
import BypassWarningBubble from './BypassWarningBubble';
import ReviewInduceRegisterTransactionBubble from './ReviewInduceRegisterTransactionBubble';
import ReviewRequestRegisterTransactionBubble from './ReviewRequestRegisterTransactionBubble';
import paymentBubbles from './PaymentBubbles';
import { useHistory } from 'react-router-dom';

interface Props {
  isOwner: boolean;
  message?: CapaQuotationChatMessage;
}

const MessageBubblePresenter: React.FC<Props> = ({ isOwner, message }) => {
  const history = useHistory();
  const messageTypeName = message?.message?.__typename;
  const messageType = message?.message?.messageType ?? undefined;

  if (messageTypeName === 'CAPAQuotationChatMessageRfq') {
    const chatMessage = message?.message as CapaQuotationChatMessageRfq;

    switch (messageType) {
      case CapaQuotationChatMessageType.ChatStart:
        return <CapaHelperBubble />;

      case CapaQuotationChatMessageType.AllowResponse:
        return <ResponseBubble message={chatMessage} />;

      case CapaQuotationChatMessageType.AllowRequest:
      case CapaQuotationChatMessageType.ConfirmRequest:
      case CapaQuotationChatMessageType.CompleteRequest:
        return <RequestBubble message={chatMessage} />;

      case CapaQuotationChatMessageType.QuotationConsultingStart: {
        return <QuotationConsultingStartBubble chatMessage={chatMessage} />;
      }
      case CapaQuotationChatMessageType.QuotationCreated: {
        return <QuotationConsultingStartBubble chatMessage={chatMessage} />;
      }
      case CapaQuotationChatMessageType.QuotationAdjustRequest: {
        return <QuotationAdjustRequestBubble message={chatMessage} />;
      }
      case CapaQuotationChatMessageType.AdjustedQuotation: {
        return <AdjustedQuotationBubble chatMessage={chatMessage} />;
      }
      case CapaQuotationChatMessageType.QuotationAgreement: {
        return <QuotationAgreementBubble chatMessage={chatMessage} />;
      }
      case CapaQuotationChatMessageType.PurchaseOrderConsultingStart: {
        return <PurchaseOrderConsultingStartBubble />;
      }
      case CapaQuotationChatMessageType.PurchaseOrderCreated: {
        return <PurchaseOrderConsultingStartBubble />;
      }
      case CapaQuotationChatMessageType.PurchaseOrderAdjustRequest:
        return <PurchaseOrderAdjustRequestBubble message={chatMessage} />;
      case CapaQuotationChatMessageType.AdjustedPurchaseOrder:
        return <AdjustedPurchaseOrderBubble />;
      case CapaQuotationChatMessageType.PurchaseOrderAgreement:
        return <PurchaseOrderAgreementBubble />;
      case CapaQuotationChatMessageType.RfqQuotationRegisterRequest: {
        return <RfqQuotationRegisterRequestBubble />;
      }

      case CapaQuotationChatMessageType.InducePurchaseOrderIssue: {
        // 발주서 발행유도
        return <InducePurchaseOrderIssueBubble />;
      }

      case CapaQuotationChatMessageType.InduceQuotationRequest: {
        //견적서 요청 유도
        return (
          <InduceQuotationRequestBubble
            senderId={message?.senderId ?? undefined}
          />
        );
      }

      case CapaQuotationChatMessageType.IntroductionConsultingStart: {
        //회사 소개서 상담시작
        return <IntroductionConsultingStartBubble />;
      }

      case CapaQuotationChatMessageType.RejectRfqPurchaseOrder: {
        // 발주서 거부
        return <RejectRfqPurchaseOrderBubble chatMessage={chatMessage} />;
      }

      case CapaQuotationChatMessageType.RequestRegisterTransactionReview: {
        // 거래 후기 작성 요청
        return (
          <ReviewRequestRegisterTransactionBubble
            chatMessage={chatMessage}
            profileId={message?.profileId ?? ''}
          />
        );
      }

      case CapaQuotationChatMessageType.InduceRegisterTransactionReview: {
        // 거래 후기 작성 유도
        return (
          <ReviewInduceRegisterTransactionBubble
            chatMessage={chatMessage}
            profileId={message?.profileId ?? ''}
            senderId={message?.senderId ?? undefined}
          />
        );
      }

      case CapaQuotationChatMessageType.PurchaseOrderRegisterRequest:
      case CapaQuotationChatMessageType.AwaitingPayment:
      case CapaQuotationChatMessageType.ApprovedPayment:
      case CapaQuotationChatMessageType.FailedPayment:
      case CapaQuotationChatMessageType.CancelRequested:
      case CapaQuotationChatMessageType.CancelConfirmed:
      case CapaQuotationChatMessageType.RejectedPurchaseOrder:
      case CapaQuotationChatMessageType.RefundRequested:
      case CapaQuotationChatMessageType.RefundConfirmed:
      case CapaQuotationChatMessageType.StartProcessing:
      case CapaQuotationChatMessageType.StartShipping:
      case CapaQuotationChatMessageType.InduceConfirmPurchase:
      case CapaQuotationChatMessageType.DelayedConfirmPurchase:
      case CapaQuotationChatMessageType.PurchaseConfirmed:
      case CapaQuotationChatMessageType.SubmallRegistrationRequest: {
        const PaymentBubble = paymentBubbles[messageType];
        if (!PaymentBubble) return null;
        return <PaymentBubble chatMessage={chatMessage} history={history} />;
      }
    }
  } else if (
    messageTypeName === 'CAPAQuotationChatMessageSimpleQuotation' ||
    messageTypeName === 'CAPAQuotationChatMessageDetailedQuotation'
  ) {
    return <QuotationBubble message={message} />;
  } else if (messageTypeName === 'CAPAQuotationChatMessageMessage') {
    const chatFiles = message?.chatFiles ?? undefined;

    if (chatFiles && chatFiles.length > 0) {
      return <FileThumbnail isOwner={isOwner} chatFiles={chatFiles} />;
    } else {
      return <DefaultBubble message={message} isOwner={isOwner} />;
    }
  } else if (messageTypeName === 'CAPAQuotationBypassMessage') {
    return <BypassWarningBubble message={message} />;
  } else if (messageTypeName === 'CAPAQuotationAnnotationCommentMessage') {
    switch (messageType) {
      case CapaQuotationChatMessageType.QuotationAnnotationComment:
        return <NewAnnotationBubble message={message} />;
      case CapaQuotationChatMessageType.QuotationAnnotationReComment:
        return <NewAnnotationCommentBubble message={message} />;
    }
  } else {
    return null;
  }
  return null;
};

export default MessageBubblePresenter;
